export type RevenueSource = 'superwall_proceeds' | 'apple_sales_estimated_proceeds' | 'apple_finance_final_proceeds' | 'hybrid'
export type ImportState = 'complete' | 'partial' | 'missing'

export interface DailyRaw {
  date: string
  appleAdsSpend: number | null
  metaAdsSpend: number | null
  appleImpressions: number | null
  metaImpressions: number | null
  appleClicks: number | null
  metaClicks: number | null
  installs: number | null
  superwallGross: number | null
  superwallProceeds: number | null
  appleEstimatedProceeds: number | null
  appleFinalProceeds: number | null
  refunds: number | null
  trialsStarted: number | null
  trialConversions: number | null
  initialPurchases: number | null
  renewals: number | null
  currency: string
  sources: { appleAds: boolean; metaAds: boolean; superwall: boolean; appleSales: boolean }
}

export interface DailyMetric extends DailyRaw {
  totalSpend: number | null
  selectedProceeds: number | null
  selectedSourceLabel: string
  netRevenue: number | null
  roas: number | null
  margin: number | null
  cac: number | null
  trialCac: number | null
  status: ImportState
  missing: string[]
  reconciliationDifference: number | null
  reconciliationPercent: number | null
  rollingSpend: number | null
  rollingProceeds: number | null
  rollingNet: number | null
  rollingRoas: number | null
}

export const safeDivide = (a: number | null, b: number | null) => a == null || b == null || b === 0 ? null : a / b
const sumNullable = (...values: (number | null)[]) => values.some(v => v == null) ? null : values.reduce<number>((sum, v) => sum + (v ?? 0), 0)

function chooseProceeds(row: DailyRaw, source: RevenueSource): [number | null, string] {
  if (source === 'superwall_proceeds') return [row.superwallProceeds, 'Superwall proceeds']
  if (source === 'apple_sales_estimated_proceeds') return [row.appleEstimatedProceeds, 'Apple estimated']
  if (source === 'apple_finance_final_proceeds') return [row.appleFinalProceeds, 'Apple final']
  if (row.appleFinalProceeds != null) return [row.appleFinalProceeds, 'Apple final (hybrid)']
  if (row.superwallProceeds != null) return [row.superwallProceeds, 'Superwall (hybrid)']
  return [row.appleEstimatedProceeds, 'Apple estimated (hybrid)']
}

export function calculateDaily(rows: DailyRaw[], source: RevenueSource): DailyMetric[] {
  const calculated = [...rows].sort((a, b) => a.date.localeCompare(b.date)).map(row => {
    const totalSpend = sumNullable(row.appleAdsSpend, row.metaAdsSpend)
    const [selectedProceeds, selectedSourceLabel] = chooseProceeds(row, source)
    const missing = [!row.sources.appleAds && 'Apple Ads', !row.sources.metaAds && 'Meta Ads', !row.sources.superwall && 'Superwall'].filter(Boolean) as string[]
    const netRevenue = sumNullable(selectedProceeds, totalSpend == null ? null : -totalSpend)
    const reconciliationDifference = row.superwallProceeds != null && row.appleEstimatedProceeds != null ? row.superwallProceeds - row.appleEstimatedProceeds : null
    return {
      ...row, totalSpend, selectedProceeds, selectedSourceLabel, netRevenue,
      roas: safeDivide(selectedProceeds, totalSpend), margin: safeDivide(netRevenue, selectedProceeds),
      cac: safeDivide(totalSpend, row.initialPurchases), trialCac: safeDivide(totalSpend, row.trialsStarted),
      status: missing.length === 0 ? 'complete' as const : missing.length === 3 ? 'missing' as const : 'partial' as const,
      missing, reconciliationDifference, reconciliationPercent: safeDivide(reconciliationDifference, row.appleEstimatedProceeds),
      rollingSpend: null, rollingProceeds: null, rollingNet: null, rollingRoas: null,
    }
  })
  return calculated.map((row, index) => {
    const window = calculated.slice(Math.max(0, index - 6), index + 1)
    if (window.length < 7) return row
    const spend = window.every(x => x.totalSpend != null) ? window.reduce((s, x) => s + (x.totalSpend ?? 0), 0) : null
    const proceeds = window.every(x => x.selectedProceeds != null) ? window.reduce((s, x) => s + (x.selectedProceeds ?? 0), 0) : null
    const net = window.every(x => x.netRevenue != null) ? window.reduce((s, x) => s + (x.netRevenue ?? 0), 0) : null
    return { ...row, rollingSpend: spend, rollingProceeds: proceeds, rollingNet: net, rollingRoas: safeDivide(proceeds, spend) }
  })
}

export type Alert = { level: 'critical' | 'warning' | 'info'; title: string; detail: string }
export function alerts(rows: DailyMetric[]): Alert[] {
  const current = rows.at(-1); const previous = rows.at(-2); if (!current) return []
  const out: Alert[] = []
  if (current.status !== 'complete') out.push({ level: 'critical', title: 'Data is incomplete', detail: `${current.date}: ${current.missing.join(', ')} missing. Profit metrics are withheld where required.` })
  if (current.netRevenue != null && current.netRevenue < 0) out.push({ level: 'critical', title: 'Negative net revenue', detail: `Proceeds did not cover paid acquisition on ${current.date}.` })
  if (current.roas != null && current.roas < 1) out.push({ level: 'warning', title: 'ROAS below 1.0×', detail: `Current blended ROAS is ${current.roas.toFixed(2)}×.` })
  if (current.reconciliationPercent != null && Math.abs(current.reconciliationPercent) > .2) out.push({ level: 'warning', title: 'Sources differ by more than 20%', detail: 'Review reporting delay, refunds, fees, taxes, and FX.' })
  if (previous?.totalSpend && current.totalSpend && current.totalSpend / previous.totalSpend > 1.3) out.push({ level: 'warning', title: 'Spend increased >30%', detail: 'Daily paid acquisition spend rose sharply.' })
  return out
}

const seeded = (index: number): DailyRaw => {
  const date = new Date(2026, 6, 9 - (20 - index)).toISOString().slice(0, 10)
  const trend = index * 19
  const partial = index === 20
  return {
    date, appleAdsSpend: 780 + trend + (index % 3) * 45, metaAdsSpend: 510 + trend * .65 + (index % 4) * 32,
    appleImpressions: 44000 + index * 900, metaImpressions: 61000 + index * 1300, appleClicks: 2480 + index * 47,
    metaClicks: 1760 + index * 39, installs: 790 + index * 18, superwallGross: partial ? null : 2450 + trend * 2.55 + (index % 3) * 90,
    superwallProceeds: partial ? null : 1990 + trend * 2.1 + (index % 3) * 70, appleEstimatedProceeds: 1925 + trend * 2.06 + (index % 4) * 52,
    appleFinalProceeds: index < 8 ? 1880 + trend * 2 : null, refunds: partial ? null : 42 + index * 2,
    trialsStarted: partial ? null : 318 + index * 8, trialConversions: partial ? null : 91 + index * 3,
    initialPurchases: partial ? null : 104 + index * 3, renewals: partial ? null : 183 + index * 5, currency: 'USD',
    sources: { appleAds: true, metaAds: true, superwall: !partial, appleSales: true }
  }
}
export const demoRows = Array.from({ length: 21 }, (_, i) => seeded(i))
